import { RUNTIME_OFFER_PLACEMENT_NUMEROLOGY_LOOKUP_BELOW_RESULT } from "@/lib/offer-placement-runtime-keys.generated";
import DepthPaywall from "@/components/DepthPaywall";
import OfferImpression from "@/components/OfferImpression";
import ReportCollectInputsCTA from "@/components/ReportCollectInputsCTA";
import { reportPlanSummaryMap } from "@ishvaram/domain-data/report-plan-summaries";

/**
 * Intent-matched paid-report offer for NUMEROLOGY lookup pages. The numerology
 * (Ank Jyotish) seeker's message-matched offer is the dedicated Numerology Life
 * Report (`numerology_report`, ₹149) — the `numerology` intent's primary offer
 * (rule 40). It replaced the earlier `brihat_kundali` upsell (2026-07-13, when
 * the Numerology Report SKU was activated): the numerology seeker wants the
 * numerology deep-dive, not the full chart report; brihat stays the secondary
 * chart upsell governed in `intent_offer_alignment.yaml`. Placed BELOW the
 * calculator result so the page still leads with its tool (rules 43/45) and
 * carries exactly one offer (rule 39).
 *
 * Acquisition mode: a numerology page has a name + DOB but not a full birth
 * chart, so the CTA routes to the kundali birth-details form
 * (`collectInputsHref = /free-kundali/`) rather than opening Razorpay with
 * incomplete report inputs. Price is read from the canonical
 * the generated plan map the Razorpay money path uses, so it can never drift
 * from what is charged without shipping the full PDP copy catalog to browsers.
 *
 * Reach is metered by the render-once `OfferImpression` island (rule 46/47):
 * `PaidReportCTA`'s own mount effect is short-circuited when `collectInputsHref`
 * is set (acquisition mode), so without this island the offer fired ZERO
 * `agent_revenue_offer_shown` and the funnel/lake read numerology as 0% reach +
 * ₹0 revenue despite the CTA being on screen — the same "wired but no meter"
 * gap `CalculatorReportBridge` was built to close. The island keys on the stable
 * plan/placement so it fires exactly once (rule 47), matching the click events.
 *
 * Copy is pain-led per rule 22 in rule-21 vocabulary (kundali, graha, mulank, bhagyank).
 */
const numerology = reportPlanSummaryMap.numerology_report;

/**
 * Bhagyank (destiny number) → its ruling graha + the life pattern that graha
 * governs. The number is engine-computed (rules 24/29 — rendered from
 * `result.bhagyank`, never a literal); the graha mapping is Ank-Jyotish
 * convention and the pain line is curated prose (rule 22), so it lives here.
 */
const BHAGYANK_GRAHA: Record<number, { en: string; hi: string }> = {
  1: {
    en: "Surya — strong will, but recognition can come late",
    hi: "Surya — mazboot iraada, par pehchaan der se",
  },
  2: {
    en: "Chandra — a sensitive mind that rarely switches off",
    hi: "Chandra — sanvedansheel mann jo shaant nahi hota",
  },
  3: {
    en: "Guru — wisdom and growth that ask for the right timing",
    hi: "Guru — gyaan aur tarakki, par sahi samay maangti hai",
  },
  4: {
    en: "Rahu — sudden ups and downs that are hard to name",
    hi: "Rahu — achanak utaar-chadhaav jinhe samajhna mushkil",
  },
  5: {
    en: "Budh — a quick mind that can scatter its focus",
    hi: "Budh — tez dimaag jiska dhyaan bikhar jaata hai",
  },
  6: {
    en: "Shukra — love and comfort that keep asking for balance",
    hi: "Shukra — prem aur aaram jo santulan maangte hain",
  },
  7: {
    en: "Ketu — a pull away from what should matter most",
    hi: "Ketu — jo cheezein maayne rakhti hain unse doori",
  },
  8: {
    en: "Shani — hard work that keeps meeting delay and tests",
    hi: "Shani — mehnat jo baar-baar der aur imtihaan se guzarti hai",
  },
  9: {
    en: "Mangal — drive and heat that need a channel",
    hi: "Mangal — energy aur garmi jise sahi disha chahiye",
  },
};

function numerologyRelevanceSubhead(bhagyank: number, language: "en" | "hi"): string | null {
  const graha = BHAGYANK_GRAHA[bhagyank];
  if (!graha) return null;
  return language === "hi"
    ? `Aapka Bhagyank ${bhagyank} ${graha.hi}. Yeh wahi graha hai jo aapke number ko chalata hai — apni poori Numerology Life Report se number, uska graha aur upay ek saath samjhein.`
    : `Your Bhagyank ${bhagyank} carries ${graha.en}. That graha rules the number — your full Numerology Life Report reads the number, its graha, name-number harmony, and the upay.`;
}

export default function NumerologyReportCTA({
  language = "en",
  sourcePath,
  bhagyank,
  name,
  birthDate,
}: {
  language?: "en" | "hi";
  /** The page the offer is shown on (for accurate attribution). */
  sourcePath: string;
  /** Engine-computed destiny number — personalizes the subhead to its graha. */
  bhagyank?: number;
  /** The name the seeker already entered on this page — carried into the
   * birth-details form so they don't re-type it (Lever 2 friction fix). */
  name?: string;
  /** The date of birth (ISO yyyy-mm-dd) the seeker already entered here — the
   * numerology form collects exactly this, so we carry it over to /free-kundali
   * instead of forcing a re-entry (the #1 leak is offer→cart, not checkout). */
  birthDate?: string;
}) {
  // Carry the already-entered name + DOB into the collect-inputs link's hash so
  // the birth-details form pre-fills them (KundaliFormCard.prefill → readDirect
  // PrefillFromUrl). The hash stays client-side (no DOB in server logs). Only add
  // a param when we actually have the value — a missing field leaves the form
  // blank exactly as before.
  const collectInputsParams: Record<string, string> = {
    intent: "chart_report",
    source: sourcePath,
    next_report: numerology.id,
  };
  if (birthDate) collectInputsParams.dob = birthDate;
  if (name) collectInputsParams.name = name;
  const collectInputsHref = `/free-kundali/#${new URLSearchParams(collectInputsParams).toString()}`;
  const carriesDob = Boolean(birthDate);

  const headline =
    language === "hi"
      ? "Aapke number theek hain, par unke peeche kaunsa graha hai?"
      : "Your numbers point at the pattern — but which graha is driving it?";
  const subhead =
    (bhagyank !== undefined ? numerologyRelevanceSubhead(bhagyank, language) : null) ??
    (language === "hi"
      ? "Mulank aur bhagyank wahi graha dikhate hain jo aapke number ko chalate hain. Apni poori Numerology Life Report se number, graha aur upay ek saath samjhein."
      : "Mulank and bhagyank are ruled by grahas. Get your full Numerology Life Report to read the number, its graha, name-number harmony, and the upay together.");

  return (
    <div className="mx-auto my-8 max-w-md px-4 sm:px-6">
      <OfferImpression
        planId={numerology.id}
        actionMode="input_collection"
        sourcePath={sourcePath}
        intent="chart_report"
        placement={RUNTIME_OFFER_PLACEMENT_NUMEROLOGY_LOOKUP_BELOW_RESULT}
        customerProblem="numerology"
        urgency="medium"
        confidence={0.8}
        routeRankScore={90}
        routeRankReason={`numerology_lookup_${numerology.id}`}
        rankedOfferOrder={[numerology.id]}
      />
      <DepthPaywall planId={numerology.id} language={language}>
        <ReportCollectInputsCTA
          planId={numerology.id}
          priceInr={numerology.price_inr}
          headline={headline}
          subhead={subhead}
          language={language}
          collectInputsHref={collectInputsHref}
          collectInputsLabel={language === "hi" ? "Janam details bharein" : "Enter birth details"}
          collectInputsNote={
            carriesDob
              ? language === "hi"
                ? "Aapki janam tithi carry over ho jaayegi — bas birth time aur place add karein, checkout khul jaayega."
                : "Your date of birth carries over — just add your birth time and place, and checkout opens."
              : language === "hi"
                ? "Pehle birth details bharein. Chart ready hote hi checkout khulega."
                : "Birth details come first. Checkout opens after the chart has the inputs needed for your report."
          }
          analyticsContext={{
            checkoutChannel: "ishvaram_report",
            commerceSystem: "ishvaram",
            intent: "chart_report",
            sourcePath,
            placement: RUNTIME_OFFER_PLACEMENT_NUMEROLOGY_LOOKUP_BELOW_RESULT,
            customerProblem: "numerology",
            urgency: "medium",
            confidence: 0.8,
            routeRankScore: 90,
            routeRankReason: `numerology_lookup_${numerology.id}`,
            rankedOfferOrder: [numerology.id],
          }}
        />
      </DepthPaywall>
    </div>
  );
}
